import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { AlarmClock, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { REPEAT_LABEL } from "@/components/tasks/TaskDialog";

const fmtDue = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return iso; } };
const localIso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return localIso(d); };
const PRESETS = [{ key: "tomorrow", label: "Tomorrow", days: 1 }, { key: "3d", label: "In 3 days", days: 3 }, { key: "week", label: "Next week", days: 7 }];

export function RecurringActions({ task, onChanged }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [until, setUntil] = useState("");

  const snooze = async (date) => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/snooze`, { until: date });
      toast.success(`Snoozed until ${fmtDue(data.due)} — the series stays on schedule`);
      setSnoozeOpen(false); onChanged?.();
    } catch (e) { console.error("snooze", e); toast.error(e?.response?.data?.detail || "Could not snooze"); }
  };
  const skip = async () => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/skip`);
      toast.success(`Skipped — next one scheduled for ${fmtDue(data.next_task.due)}`);
      setSkipOpen(false); onChanged?.();
    } catch (e) { console.error("skip", e); toast.error(e?.response?.data?.detail || "Could not skip"); }
  };

  return (
    <>
      <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <PopoverTrigger asChild>
          <button className="text-muted-foreground hover:text-primary shrink-0" title="Snooze this occurrence" data-testid={`task-snooze-${task.id}`}><AlarmClock className="h-3.5 w-3.5" /></button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end" data-testid="snooze-popover">
          <div className="text-xs font-semibold mb-2 truncate">Snooze “{task.title}”</div>
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            {PRESETS.map((p) => <Button key={p.key} variant="outline" size="sm" className="h-7 text-xs px-1" data-testid={`snooze-${p.key}`} onClick={() => snooze(inDays(p.days))}>{p.label}</Button>)}
          </div>
          <div className="flex gap-1.5">
            <Input type="date" className="h-8 text-xs" min={inDays(0)} value={until} onChange={(e) => setUntil(e.target.value)} data-testid="snooze-date" />
            <Button size="sm" className="h-8" disabled={!until} onClick={() => snooze(until)} data-testid="snooze-date-go">Go</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Only this occurrence moves — the series keeps its normal schedule.</p>
        </PopoverContent>
      </Popover>
      <Popover open={skipOpen} onOpenChange={setSkipOpen}>
        <PopoverTrigger asChild>
          <button className="text-muted-foreground hover:text-primary shrink-0" title="Skip this occurrence" data-testid={`task-skip-${task.id}`}><SkipForward className="h-3.5 w-3.5" /></button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end" data-testid="skip-popover">
          <div className="text-xs font-semibold mb-1">Skip this occurrence?</div>
          <p className="text-[11px] text-muted-foreground mb-2.5">It’s closed as skipped (not completed) and the next {(REPEAT_LABEL[task.repeat] || "").toLowerCase()} one is scheduled automatically.</p>
          <Button size="sm" className="w-full h-8" onClick={skip} data-testid={`task-skip-confirm-${task.id}`}><SkipForward className="h-3.5 w-3.5 mr-1.5" /> Skip it</Button>
        </PopoverContent>
      </Popover>
    </>
  );
}
